import { LANGUAGES, STAKE_VALUES } from '../constants/defaultValues';
import { findName, formatSpecifierName, formatTeamNames } from './render';
import { producerTypeEnum, EVENT_TYPE_ENUM } from '../constants/enums';
import { store } from '../store';

const generateCommonBetLineProps = ({ event, market, outcome }) => {
    const reduxStore = store.getState();

    const minStakeAmount = reduxStore.settings.account.data?.minBettingStakeAmount || STAKE_VALUES.min;

    const descriptions = reduxStore.descriptions.data;
    const locale = reduxStore.settings.locale;
    const { programCode, teams = {}, live, playerNames } = event;
    const { id: marketId, marketRefId, specifiers, marketHash, mbn, status } = market;
    const { outcomeRefId, odd, status: outcomeStatus } = outcome;
    const marketDescriptions = descriptions.markets;
    const outcomeDescriptions = descriptions.outcomes;

    const tournament = reduxStore.sportEvents.filters.leagues.find(league => league.id === event.seasonRefId);
    const tournamentName = tournament?.names[locale] || tournament?.names[LANGUAGES.eng];

    let marketName = '';
    let marketTemplateName = '';
    let marketLabel = null;
    let outcomeName = '';
    let outcomeTemplateName = '';
    const playerName = playerNames?.[outcomeRefId];

    if (market.dynamic) {
        marketName = findName(market.names, locale);
        marketTemplateName = findName(market.names, LANGUAGES.eng);
        outcomeName = findName(market.outcomeNames.find(outcome => outcome.id === outcomeRefId)?.names, locale);
        outcomeTemplateName = findName(
            market.outcomeNames.find(outcome => outcome.id === outcomeRefId)?.names,
            LANGUAGES.eng
        );
    } else {
        if (market.names && market.outcomeNames) {
            marketName = findName(market.names, locale);
            marketTemplateName = findName(market.names, LANGUAGES.eng);
            outcomeName = findName(market.outcomeNames.find(outcome => outcome.id === outcomeRefId)?.names, locale);
            outcomeTemplateName = findName(
                market.outcomeNames.find(outcome => outcome.id === outcomeRefId)?.names,
                LANGUAGES.eng
            );
            marketLabel = market.names[LANGUAGES.eng];
        } else {
            marketName = findName(marketDescriptions[marketRefId]?.names, locale);
            marketTemplateName = findName(marketDescriptions[marketRefId]?.names, LANGUAGES.eng);
            outcomeName = findName(outcomeDescriptions[outcomeRefId]?.names, locale);
            outcomeTemplateName = findName(outcomeDescriptions[outcomeRefId]?.names, LANGUAGES.eng);
            marketLabel = marketDescriptions[marketRefId]?.labels[LANGUAGES.eng];
        }
    }

    const formattedMarketName = formatSpecifierName({
        name: marketName,
        specifiers,
        teams: formatTeamNames(teams, locale),
        tournamentName,
        playerNames,
    });

    let formattedMarketTemplateName = formatSpecifierName({
        name: marketTemplateName,
        specifiers,
        teams: formatTeamNames(teams, LANGUAGES.eng),
        tournamentName: tournament?.names[LANGUAGES.eng],
        playerNames,
    });

    const formattedOutcomeName = formatSpecifierName({
        name: outcomeName || playerName,
        specifiers,
        teams: formatTeamNames(teams, locale),
        tournamentName,
        playerNames,
    });

    const formattedOutcomeTemplateName = formatSpecifierName({
        name: outcomeTemplateName || playerName,
        specifiers,
        teams: formatTeamNames(teams, LANGUAGES.eng),
        tournamentName: tournament?.names[LANGUAGES.eng],
        playerNames,
    });

    if (marketTemplateName === formattedMarketTemplateName && market.specifiers?.length === 1) {
        const specifierValue = market.specifiers[0].value;
        formattedMarketTemplateName += ` ${specifierValue}`;
    }

    return {
        eventId: event.id,
        programCode: programCode,
        teams: teams,
        mbn: mbn,
        odd: odd,
        closed: market.closed || outcome.closed,
        currentOdd: odd,
        changeType: '',
        marketId: marketId,
        outcomeId: outcomeRefId,
        marketRefId: marketRefId,
        marketStatus: status,
        outcomeName: formattedOutcomeName,
        outcomeStatus: outcomeStatus,
        marketName: formattedMarketName,
        marketTemplateName: formattedMarketTemplateName,
        outcomeTemplateName: formattedOutcomeTemplateName,
        marketLabel: marketLabel,
        marketHash: marketHash,
        stake: minStakeAmount,
        isLive: live,
        producerType: live ? producerTypeEnum.LIVE : producerTypeEnum.PRE_MATCH,
        banco: false,
        marketVersion: 'string',
        frozen: market.frozen,
        sportTypeId: event.sportTypeRefId,
        tournamentId: event.seasonRefId,
    };
};

export const generateRegularBetLine = ({ event, market, outcome }) => {
    return {
        ...generateCommonBetLineProps({ event, market, outcome }),
        eventId: event.id,
        tournamentId: event.seasonRefId,
        eventType: EVENT_TYPE_ENUM.regular,
    };
};

export const generateOutrightBetLine = ({ event, market, outcome }) => {
    return {
        ...generateCommonBetLineProps({ event, market, outcome }),
        eventId: event.currentSeasonId,
        tournamentId: event.id,
        eventName: `${event.seasonName} ${event.year}`,
        isLive: false,
        eventType: EVENT_TYPE_ENUM.outright,
    };
};
